/**
 * Checkout Types
 *
 * These types are used for the checkout state and operations.
 * They are compatible with the Saleor/OrderApp API responses.
 */

// Money types
export interface Money {
  amount: number;
  currency: string;
}

export interface TaxedMoney {
  gross: Money;
  net: Money;
  tax: Money;
  currency: string;
}

// Address type
export interface CheckoutAddress {
  id?: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  streetAddress1: string;
  streetAddress2?: string;
  city: string;
  cityArea?: string;
  postalCode: string;
  countryArea?: string;
  phone?: string;
  country: {
    code: string;
    country: string;
  };
  isDefaultBillingAddress?: boolean;
  isDefaultShippingAddress?: boolean;
  metadata?: Array<{ key: string; value: string }>;
}

// Dropdown attribute value (for caseMaterial, color, size, etc.)
export interface DropdownAttributeValue {
  name?: string | null;
  plainText?: string | null;
  slug?: string | null;
  translation?: {
    name?: string | null;
    plainText?: string | null;
  } | null;
}

export interface DropdownAttribute {
  attribute?: {
    slug?: string | null;
  } | null;
  values?: DropdownAttributeValue[] | null;
}

// Multiselect attribute (for compatible straps, etc.)
export interface MultiselectAttribute {
  values?: Array<{
    name?: string | null;
    slug?: string | null;
  }> | null;
}

// Product type in line
export interface LineProduct {
  id: string;
  name: string;
  slug: string;
  translation?: {
    name: string;
  } | null;
  productType?: {
    id: string;
    name: string;
    slug: string;
  } | null;
  thumbnail?: {
    url: string;
    alt: string;
  } | null;
  // Watch attributes for variant display (e.g., "Stainless steel - Black")
  caseMaterial?: DropdownAttribute | null;
  color?: DropdownAttribute | null;
  // Strap/accessory attributes for variant display (e.g., "644X - Standard")
  size?: DropdownAttribute | null;
  // Compatible straps for upsell (slugs used to query available straps)
  compatibleStraps?: MultiselectAttribute | null;
  // Warranty extension duration for warranty upsell button (e.g., "2" or "3" years)
  warrantyExtensionDuration?: DropdownAttribute | null;
  // Strap color attribute - "Metal bracelet" indicates need for wrist size selection
  strapColor?: DropdownAttribute | null;
}

// Product variant in line
export interface LineVariant {
  id: string;
  sku: string | null;
  name: string;
  product: LineProduct | null;
  pricing?: {
    price?: {
      gross: Money;
    } | null;
  } | null;
}

// Checkout line
export interface CheckoutLineData {
  id: string;
  quantity: number;
  unitPrice: TaxedMoney;
  totalPrice: TaxedMoney;
  undiscountedTotalPrice?: Money;
  undiscountedUnitPrice?: Money;
  variant: LineVariant | null;
  // Metafields
  relatedProductBuckleId?: string | null;
  relatedProductStrapId?: string | null;
  relatedProductWatchId?: string | null;
  strapPackReference?: string | null;
  hasWarrantyExtension?: string | null;
  // Bracelet/wrist size for metal bracelet watches (e.g., "165mm" or "6,5in")
  relatedBraceletSize?: string | null;
  // Metadata array
  metadata?: Array<{ key: string; value: string }>;
}

// Channel
export interface Channel {
  id: string;
  slug: string;
  name: string;
  currencyCode: string;
}

// Shipping method
export interface ShippingMethod {
  id: string;
  name: string;
  description?: string | null;
  price: Money;
  minimumDeliveryDays?: number;
  maximumDeliveryDays?: number;
}

// Delivery method
export type DeliveryMethod =
  | { __typename: 'ShippingMethod'; id: string; name: string }
  | { __typename: 'Warehouse'; id: string; name: string };

// User
export interface CheckoutUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

// Full checkout data (matches Saleor Checkout fragment)
export interface CheckoutData {
  id: string;
  email: string | null;
  languageCode?: string;
  user: CheckoutUser | null;
  channel: Channel;
  lines: CheckoutLineData[];
  billingAddress: CheckoutAddress | null;
  shippingAddress: CheckoutAddress | null;
  shippingMethods: ShippingMethod[];
  deliveryMethod: DeliveryMethod | null;
  isShippingRequired: boolean;
  voucherCode: string | null;
  discount: Money | null;
  discountName: string | null;
  subtotalPrice: TaxedMoney;
  shippingPrice: TaxedMoney;
  totalPrice: TaxedMoney;
  totalBalance: Money | null;
  isGift: boolean;
  giftFrom: string | null;
  giftMessage: string | null;
  orderType?: string | null;
  preOrderData?: string | null;
}

// Error types
export interface ApiError {
  field?: string | null;
  code: string;
  message: string;
  context?: Record<string, unknown> | null;
}

export type ApiErrorType = 'checkout' | 'orderApp' | 'payment' | 'address';

export interface ApiErrorWithType extends ApiError {
  type: ApiErrorType;
}

// Line input for adding items
export interface CheckoutLineInput {
  variantId: string;
  quantity: number;
  metadata?: Array<{ key: string; value: string }>;
  watchLineId?: string;
  strapRelatedProducts?: {
    buckleId: string;
  };
}

// ============================================================================
// Address Form Types (for checkout forms)
// ============================================================================

/**
 * Address form data structure for checkout forms
 */
export interface AddressFormData {
  email?: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  streetAddress1: string;
  streetAddress2?: string;
  city: string;
  postalCode: string;
  country: string;
  countryArea?: string;
  phone?: string;
}

/**
 * Billing-specific address (no email)
 */
export type BillingAddressFormData = Omit<AddressFormData, 'email'>;

/**
 * Shipping address with required email (for checkout delivery form)
 */
export type ShippingAddressFormData = Omit<AddressFormData, 'email'> & { email: string };

/**
 * Form validation errors by field
 */
export type AddressFormErrors = Partial<Record<keyof AddressFormData, string>>;

/**
 * Default empty address form data
 */
export const EMPTY_ADDRESS_FORM: AddressFormData = {
  email: '',
  firstName: '',
  lastName: '',
  streetAddress1: '',
  streetAddress2: '',
  city: '',
  postalCode: '',
  country: 'US',
  countryArea: '',
  phone: '',
};

/**
 * Default empty billing address
 */
export const EMPTY_BILLING_FORM: BillingAddressFormData = {
  firstName: '',
  lastName: '',
  streetAddress1: '',
  streetAddress2: '',
  city: '',
  postalCode: '',
  country: 'US',
  countryArea: '',
  phone: '',
};

// ============================================================================
// Japan-Specific Form Types
// ============================================================================

/**
 * Salutation values for JP checkout (Male/Female only — no Mrs/Mr/Ms)
 */
export type Salutation = 'male' | 'female' | '';

/**
 * Japan-specific address form fields that are managed separately
 * and merged into the standard address payload on submission.
 */
export interface JpFormData {
  /** Male / Female salutation */
  salutation: Salutation;
  /** カタカナ姓 — Last name phonetic reading */
  lastNameKatakana: string;
  /** カタカナ名 — First name phonetic reading */
  firstNameKatakana: string;
  /** ローマ字姓 — Last name in Latin script (for card processing / labels) */
  lastNameRomaji: string;
  /** ローマ字名 — First name in Latin script */
  firstNameRomaji: string;
  /** マンション名 — House or mansion name (optional) */
  houseName: string;
  /** 部屋番号 — Apartment / room number (optional) */
  apartmentNumber: string;
  /** Preferred delivery time slot */
  deliveryTimeSlot: string;
  /** Join the club (opt-in checkbox) */
  joinClub: boolean;
  /** Include a receipt with the shipment */
  includeReceipt: boolean;
}

/**
 * Empty JP form defaults
 */
export const EMPTY_JP_FORM: JpFormData = {
  salutation: '',
  lastNameKatakana: '',
  firstNameKatakana: '',
  lastNameRomaji: '',
  firstNameRomaji: '',
  houseName: '',
  apartmentNumber: '',
  deliveryTimeSlot: '',
  joinClub: false,
  includeReceipt: false,
};

export enum DELIVERY_MODES {
  BOUTIQUE = 'boutiquePickUp',
  HOME = 'homeDelivery',
}
